import React, { useEffect, useState, useRef } from 'react';
import TruncatedText from './TruncatedText';
import FeedbackPanel from './FeedbackPanel';
import { gradeApi } from '../../../api.js';
import { XMarkIcon, ExclamationCircleIcon, EyeIcon } from '@heroicons/react/24/outline';
import { Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';

// Define styling variables to match the SCSS inspiration
const COLORS = {
  primary: '#e4b17a',
  primaryDark: '#c99b69',
  textPrimary: '#8c6b4a',
  textSecondary: '#a17c57',
  bgLight: '#fdf8f3',
  bgMedium: '#f9ede2',
  bgWhite: '#ffffff',
  green: '#10b981',
  gray: '#d1d5db',
  lightGray: '#f3f4f6'
};

// Add animation styles
const animationStyles = {
  enter: 'transform-gpu scale-95 opacity-0',
  enterActive: 'transition-all duration-300 ease-out transform-gpu scale-100 opacity-100',
  exit: 'transform-gpu scale-100 opacity-100',
  exitActive: 'transition-all duration-200 ease-in transform-gpu scale-95 opacity-0',
  pinAppear: 'animate-pin-appear',
  scaleEntrance: 'animate-scale-entrance'
};

// Define keyframes animations in a style tag
const StyleTag = () => (
  <style jsx global>{`
    /* Keyframes for entrance animations */

    @keyframes pulse-glow {
      0%,
      100% {
        box-shadow: 0 0 0 2px rgba(228, 177, 122, 0.1);
      }
      50% {
        box-shadow: 0 0 0 4px rgba(228, 177, 122, 0.2);
      }
    }

    @keyframes slide-in {
      0% {
        transform: translateX(-10px);
        opacity: 0;
      }
      100% {
        transform: translateX(0);
        opacity: 1;
      }
    }

    .scale-dot {
      transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    .scale-dot.active {
      animation: pulse-glow 2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
    }

    .scale-fill {
      transition: width 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    .score-display {
      animation: slide-in 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    }
  `}</style>
);

const GradingRubric = ({
  examPaper,
  parentQuestion, // Added to accept parent question data directly
  regradeInProgress,
  pinnedSubQuestionId,
  togglePinSubQuestion,
  handleRubricItemChange,
  getFeedback,
  getInstructorFeedback,
  isEditing,
  handleRemoveFeedback,
  handleEditFeedback,
  handleFeedbackChange,
  handleFeedbackSave,
  handleFeedbackCancel,
  regradeRef,
  getRR,
  handleRegradeClick,
  questionIndex, // Optional - will be used if provided
  subQuestionIndex, // Optional - will be used if provided
  subQuestion, // Optional - will be used if provided
  assignment_id,
  user,
  setMessage,
  getQuestions,
  getAssignmentSubmission,
  regrade_question_id, // Added back the props reference for regrade_question_id
  sortedSubmissionIds,
  submission_id,
  getRRText,
  handleRegradeReq,
  updateExamPaper, // Added this prop
  uploadQuestions // Added this prop
}) => {
  const currentSubmissionIndex = sortedSubmissionIds
    ? sortedSubmissionIds.indexOf(Number(submission_id))
    : -1;
  const remainingSubmissionIds =
    currentSubmissionIndex >= 0 && sortedSubmissionIds
      ? sortedSubmissionIds.slice(currentSubmissionIndex)
      : [];
  const remainingCount = remainingSubmissionIds.length - 1;

  // Track which subquestions have been viewed locally
  const [viewedSubQuestions, setViewedSubQuestions] = useState({});
  const [viewingTimes, setViewingTimes] = useState({});
  const viewTimers = useRef({});
  const REQUIRED_VIEW_TIME = 2000; // 2 seconds required viewing time

  // State for AI Edit Popup
  const [showAiEditPopup, setShowAiEditPopup] = useState(false);
  const [aiEditInput, setAiEditInput] = useState('');
  const [isProcessingAiEdit, setIsProcessingAiEdit] = useState(false);
  const aiEditInputRef = useRef(null);
  const [activeSubQuestion, setActiveSubQuestion] = useState(null);

  // Track regrade polling
  const regradePollingTimers = useRef({});
  const REGRADE_POLLING_INTERVAL = 5000; // Check every 5 seconds
  const MAX_POLLING_TIME = 120000; // Stop polling after 2 minutes (24 attempts)

  // Function to mark a subquestion as viewed
  const markAsViewed = subquestionId => {
    if (!subquestionId || !submission_id || !assignment_id) {
      return;
    }

    // Call the API to mark the feedback as viewed
    gradeApi
      .post({
        path: `/api/assignment/${assignment_id}/submissions/${submission_id}/feedback/${subquestionId}/mark-viewed/`,
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      })
      .then(response => {
        console.log('Feedback marked as viewed:', response);
        // Update local state to show the viewed indicator immediately
        setViewedSubQuestions(prev => ({
          ...prev,
          [subquestionId]: true
        }));
      })
      .catch(error => {
        console.error('Error marking feedback as viewed:', error);
      });
  };

  // Function to check if a subquestion should be marked as viewed
  const isSubQuestionViewed = subquestionId => {
    // First check if it's already viewed in the feedback data
    const feedback = getFeedback && getFeedback(subquestionId);
    if (feedback?.is_viewed) {
      return true;
    }

    // Then check if it's been marked as viewed in this session
    return viewedSubQuestions[subquestionId];
  };

  // Create refs for intersection observer
  const subQuestionRefs = useRef({});

  // Set up intersection observer to detect when subquestions come into view
  useEffect(() => {
    const observerOptions = {
      root: null, // viewport
      rootMargin: '0px',
      threshold: 0.6 // 60% of the element needs to be visible
    };

    const handleIntersection = (entries, observer) => {
      entries.forEach(entry => {
        const subquestionId = entry.target.dataset.subquestionId;
        if (!subquestionId) {
          return;
        }

        if (entry.isIntersecting) {
          // Start tracking time when the element becomes visible
          const startTime = Date.now();
          viewTimers.current[subquestionId] = {
            startTime,
            timer: setInterval(() => {
              const currentTime = Date.now();
              const elapsedTime = currentTime - startTime;

              // Update the viewing time state
              setViewingTimes(prev => ({
                ...prev,
                [subquestionId]: elapsedTime
              }));

              // If viewing time exceeds threshold, mark as viewed
              if (elapsedTime >= REQUIRED_VIEW_TIME) {
                clearInterval(viewTimers.current[subquestionId].timer);
                delete viewTimers.current[subquestionId];
                markAsViewed(subquestionId);
              }
            }, 500) // Update viewing time every 500ms
          };
        } else {
          // Clear timer when element goes out of view
          if (viewTimers.current[subquestionId]) {
            clearInterval(viewTimers.current[subquestionId].timer);
            delete viewTimers.current[subquestionId];

            // Reset viewing time for this subquestion
            setViewingTimes(prev => ({
              ...prev,
              [subquestionId]: 0
            }));
          }
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersection, observerOptions);

    // Get all subquestion elements and observe them
    const subQuestionElements = document.querySelectorAll('[data-subquestion-id]');
    subQuestionElements.forEach(element => {
      observer.observe(element);
    });

    return () => {
      // Clean up all timers when component unmounts
      Object.values(viewTimers.current).forEach(timerInfo => {
        if (timerInfo.timer) {
          clearInterval(timerInfo.timer);
        }
      });
      observer.disconnect();
    };
  }, [examPaper, submission_id]); // Re-run when examPaper or submission changes

  // Add useEffect to mark feedback as viewed when a subquestion is pinned
  useEffect(() => {
    if (pinnedSubQuestionId) {
      // Add a delay before marking as viewed to allow seeing the animation
      setTimeout(() => {
        markAsViewed(pinnedSubQuestionId);
      }, 2000); // 2 second delay
    }
  }, [pinnedSubQuestionId]);

  // Function to start polling for regrade completion
  const startPollingForRegradeCompletion = subquestionId => {
    // Clear any existing timer first to prevent duplicates
    if (regradePollingTimers.current[subquestionId]) {
      clearInterval(regradePollingTimers.current[subquestionId]);
      delete regradePollingTimers.current[subquestionId];
    }

    let pollingCount = 0;
    const startTime = Date.now();

    console.log(`Starting polling for regrade completion of subquestion ${subquestionId}`);

    const checkRegradeStatus = async () => {
      try {
        // Fetch the latest questions to check regrade status
        const response = await gradeApi.get({
          path: `/api/assignment/${assignment_id}/questions/`,
          config: {
            headers: {
              Authorization: `Bearer ${user?.session_token}`
            }
          }
        });

        // Find the subquestion in the response to check its regrade_in_progress status
        let isStillRegrading = false;
        if (response && response.questions) {
          for (const question of response.questions) {
            const subQ = question.subQuestions?.find(sq => sq.id == subquestionId);
            if (subQ) {
              isStillRegrading = subQ.regrade_in_progress || false;
              break;
            }
          }
        }

        if (!isStillRegrading) {
          console.log(`Regrade for subquestion ${subquestionId} completed, refreshing all data...`);

          // Clear the polling timer first
          clearInterval(regradePollingTimers.current[subquestionId]);
          delete regradePollingTimers.current[subquestionId];

          // Refresh all data
          if (getQuestions) {
            await getQuestions();
          }

          if (getAssignmentSubmission) {
            await getAssignmentSubmission();
          }

          // Show success message
          // toast.success('AI-Regrade completed! Scores and feedback have been updated.');
        } else if (Date.now() - startTime > MAX_POLLING_TIME) {
          // Stop polling if maximum time reached
          console.log(`Reached maximum polling time for subquestion ${subquestionId}`);
          clearInterval(regradePollingTimers.current[subquestionId]);
          delete regradePollingTimers.current[subquestionId];

          // Show timeout message
          toast.error(
            'Regrade is taking longer than expected. Please refresh the page to check the status.'
          );
        }

        pollingCount++;
      } catch (error) {
        console.error('Error checking regrade status:', error);

        // On error, stop polling after a few retries
        if (pollingCount > 3) {
          clearInterval(regradePollingTimers.current[subquestionId]);
          delete regradePollingTimers.current[subquestionId];
          toast.error('Error checking regrade status. Please refresh the page.');
        }
      }
    };

    // Start polling immediately
    checkRegradeStatus();

    // Then set up interval for subsequent checks
    regradePollingTimers.current[subquestionId] = setInterval(
      checkRegradeStatus,
      REGRADE_POLLING_INTERVAL
    );
  };

  // Clean up polling timers on component unmount
  useEffect(() => {
    return () => {
      // Clear all regrade polling timers
      Object.values(regradePollingTimers.current).forEach(timer => {
        clearInterval(timer);
      });
    };
  }, []);

  // Monitor changes to regradeInProgress
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      Object.entries(regradeInProgress).forEach(([subquestionId, isRegrading]) => {
        if (isRegrading && !regradePollingTimers.current[subquestionId]) {
          startPollingForRegradeCompletion(subquestionId);
        }
      });
    }, 5000); // Small debounce

    return () => clearTimeout(timeoutId);
  }, [regradeInProgress]);

  const handleAiEditClick = subQuestion => {
    setActiveSubQuestion(subQuestion);
    setShowAiEditPopup(true);
    setAiEditInput('');
    // Focus the textarea
    setTimeout(() => {
      if (aiEditInputRef.current) {
        aiEditInputRef.current.focus();
      }
    }, 100);
  };

  const enhance_single_rubric = async () => {
    if (!activeSubQuestion) {
      toast.error('No active subquestion selected for AI enhancement.');
      return;
    }

    setIsProcessingAiEdit(true);

    try {
      // Determine the question text from available sources
      let questionText = '';
      let solution = activeSubQuestion.solution || '';
      let weight = Number(activeSubQuestion.max_weight) || 0;

      // Try to get question text from parentQuestion prop first (from TQuestionDetails.js)
      if (Array.isArray(examPaper) && examPaper.length > 0) {
        // Find the parent question in the array
        const foundQuestion = examPaper.find(
          q => q.subQuestions && q.subQuestions.some(sq => sq.id === activeSubQuestion.id)
        );
        if (foundQuestion) {
          questionText = foundQuestion.description;
        }
      }

      // Final fallback - use a placeholder
      if (!questionText) {
        toast('Using default question text. AI enhancement may be less effective.', {
          icon: '⚠️'
        });
        questionText = 'Question text not available';
      }

      const payload = {
        instruction: aiEditInput,
        questionText: questionText,
        solution: solution,
        weight: Number(weight)
      };

      console.log('Sending payload to AI enhance-rubric:', payload);

      const response = await gradeApi.post({
        path: '/api/ai/enhance-rubric/',
        body: payload,
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });

      console.log('AI enhancement response:', response.data);

      // Extract the enhanced rubric data from the response
      // Handle different response structures
      const enhancedRubric = response.data.data || response.data;

      if (enhancedRubric) {
        // Get the grading criteria from the response
        const gradingCriteria = enhancedRubric.gradingCriteria || enhancedRubric.grading_criteria;
        // Get the weight from the response
        const questionWeight = enhancedRubric.questionWeight || enhancedRubric.weight;

        if (gradingCriteria) {
          // Update the active subquestion with the enhanced rubric data
          const updatedSubQuestion = {
            ...activeSubQuestion,
            grading_criteria: gradingCriteria
          };

          // Update weight if it was changed
          if (questionWeight) {
            updatedSubQuestion.max_weight = Number(questionWeight);
          }

          console.log('Updated subquestion:', updatedSubQuestion);

          // Update the editedSolutions state with the new grading criteria
          setEditedSolutions(prev => ({
            ...prev,
            [activeSubQuestion.id]: gradingCriteria
          }));

          // Update the DOM element's data-max-weight attribute
          if (questionWeight) {
            const element = document.getElementById(`subquestion-${activeSubQuestion.id}`);
            if (element) {
              element.setAttribute('data-max-weight', Number(questionWeight).toString());
            }
            
            // Mark this weight as changed
            setChangedMaxWeights(prev => ({
              ...prev,
              [activeSubQuestion.id]: true
            }));
          }

          // Find and update the subquestion in the examPaper
          if (Array.isArray(examPaper)) {
            // For array structure (NewTDetails.js)
            const updatedExamPaper = examPaper.map(question => {
              if (
                question.subQuestions &&
                question.subQuestions.some(sq => sq.id === activeSubQuestion.id)
              ) {
                return {
                  ...question,
                  subQuestions: question.subQuestions.map(sq =>
                    sq.id === activeSubQuestion.id ? updatedSubQuestion : sq
                  )
                };
              }
              return question;
            });

            // If we have a function to update the examPaper state, call it
            if (updateExamPaper) {
              updateExamPaper(updatedExamPaper);
            }
          }

          // Call uploadQuestions directly to save changes to backend
          if (uploadQuestions) {
            await uploadQuestions();
          }

          toast.success('Rubric enhanced successfully!');
        } else {
          toast.error('The AI response did not contain updated grading criteria.');
        }
      } else {
        toast.error('Failed to enhance rubric. Please try again.');
      }
    } catch (error) {
      console.error('Error while processing AI edit:', error);
      toast.error(
        error.response?.data?.error || 'Failed to process your request. Please try again.'
      );
    } finally {
      setIsProcessingAiEdit(false);
      setShowAiEditPopup(false);
      setAiEditInput('');
      setActiveSubQuestion(null);
    }
  };

  // ScoreScale component - A scale-based grading system inspired by Google Forms
  const ScoreScale = ({ subQuestion }) => {
    // Register the element in refs system when it mounts
    useEffect(() => {
      const element = document.getElementById(`subquestion-${subQuestion.id}`);
      if (element) {
        // Note: rubricItemRefs is passed through props, assumed to be a ref object
      }
    }, [subQuestion.id]);

    // Find the max possible score from the max_weight of the subquestion
    const initialMaxScore = subQuestion.max_weight || 0;
    const [maxScore, setMaxScore] = useState(initialMaxScore);
    const simplifyScale = maxScore > 8; // Threshold for simplifying

    // Get the student-specific feedback using the prop function
    const studentFeedback = getFeedback(subQuestion.id);

    // console.log(studentFeedback);

    // Determine the score to display, prioritizing the student-specific feedback score
    let displayScore = 0;
    if (studentFeedback) {
      displayScore = parseFloat(studentFeedback.score);
      // Ensure we handle NaN correctly, default to 0 if parsing fails
    }

    // === NEW STATE for editing ===
    const [isEditingScore, setIsEditingScore] = useState(false);
    const [editValue, setEditValue] = useState(displayScore.toString()); // Store input value as string
    const [isEditingMaxScore, setIsEditingMaxScore] = useState(false);
    const [editMaxValue, setEditMaxValue] = useState(maxScore.toString());
    const [tempMaxScore, setTempMaxScore] = useState(maxScore); // Store the temporary edited max score

    // Update editValue if displayScore changes externally (e.g., via slider click)
    useEffect(() => {
      if (!isEditingScore) {
        setEditValue(displayScore.toString());
      }
    }, [displayScore, isEditingScore]);

    // Initialize maxScore from subQuestion.max_weight when the component mounts or when it changes significantly
    useEffect(() => {
      // Only update if not in editing mode
      if (!editingSolutionId || editingSolutionId !== subQuestion.id) {
        setMaxScore(initialMaxScore);
        setEditMaxValue(initialMaxScore.toString());
        setTempMaxScore(initialMaxScore);
      }
    }, [initialMaxScore, subQuestion.id, editingSolutionId]);

    // Make max score editable when fine tune is clicked
    useEffect(() => {
      if (editingSolutionId === subQuestion.id) {
        // Don't automatically set isEditingMaxScore to true when editingSolutionId changes
        // This way, max score editing only starts when explicitly clicked

        // But DO load the stored value from the data attribute if it exists
        const element = document.getElementById(`subquestion-${subQuestion.id}`);
        if (element) {
          const dataMaxWeight = element.getAttribute('data-max-weight');
          if (dataMaxWeight) {
            const parsedValue = parseFloat(dataMaxWeight);
            if (!isNaN(parsedValue) && parsedValue >= 0) {
              // Update all relevant state variables to ensure consistency
              setMaxScore(parsedValue);
              setEditMaxValue(parsedValue.toString());
              setTempMaxScore(parsedValue);
            }
          }
        }
      }
    }, [editingSolutionId, subQuestion.id]);
    // === END NEW STATE ===

    // Create score points - adapt increment based on maxScore
    const scorePoints = [];
    const increment = simplifyScale ? 1 : 0.5; // Increment by 1 if maxScore > 8

    // Use tempMaxScore for calculating points instead of maxScore
    const effectiveMaxScore = editingSolutionId === subQuestion.id ? tempMaxScore : maxScore;
    for (let i = 0; i <= effectiveMaxScore; i += increment) {
      scorePoints.push(i);
    }

    // Reference for drag handling
    const sliderRef = useRef(null);
    const [hoverScore, setHoverScore] = useState(null); // Track score being hovered for visual feedback

    // Simple click handler for dots - just update the score
    const handleDotClick = score => {
      // console.log('Dot clicked with score:', score);

      // Update the backend - This function (passed from parent) should handle local state update
      if (handleRubricItemChange) {
        handleRubricItemChange(subQuestion.id, score)();
      }
    };

    // Just handle hover over the slider - no drag functionality
    const handleSliderHover = e => {
      if (!sliderRef.current) {
        return;
      }

      const slider = sliderRef.current;
      const rect = slider.getBoundingClientRect();
      const percentage = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const score = Math.round(percentage * maxScore * 2) / 2; // Round to nearest 0.5

      setHoverScore(score);
    };

    // Simple click handler for the slider
    const handleSliderClick = e => {
      if (!sliderRef.current) {
        return;
      }

      // Calculate score from click position
      const slider = sliderRef.current;
      const rect = slider.getBoundingClientRect();
      const percentage = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const score = Math.round(percentage * maxScore * 2) / 2; // Round to nearest 0.5

      // Use the same click handler as the dots
      handleDotClick(score);
    };

    // Handle mouse leave to clear hover state
    const handleMouseLeave = () => {
      setHoverScore(null);
    };

    // === NEW HANDLERS for input ===
    const handleScoreClick = () => {
      setEditValue(displayScore.toString()); // Initialize with current score
      setIsEditingScore(true);
      // Maybe focus the input shortly after render? (Requires ref)
    };

    const handleInputChange = e => {
      setEditValue(e.target.value);
    };

    const saveEditedScore = () => {
      let newScore = parseFloat(editValue);
      if (isNaN(newScore)) {
        newScore = displayScore; // Revert if input is not a number
      } else {
        // Clamp the score
        // === UPDATED: Round to max 2 decimal places ===
        newScore = Math.round(newScore * 100) / 100;
        // === REMOVED: Rounding to nearest 0.5 ===
        // newScore = Math.round(newScore * 2) / 2;
      }
      setIsEditingScore(false);
      // Use existing handler to update score if it changed
      if (newScore !== displayScore) {
        handleDotClick(newScore);
      }
      // Reset editValue in case clamping/rounding occurred
      setEditValue(newScore.toString());
    };

    const handleInputBlur = () => {
      saveEditedScore();
    };

    const handleInputKeyDown = e => {
      if (e.key === 'Enter') {
        saveEditedScore();
      } else if (e.key === 'Escape') {
        setEditValue(displayScore.toString()); // Revert to original score
        setIsEditingScore(false);
      }
    };
    // === END NEW HANDLERS ===

    // === NEW HANDLERS for max score input ===
    const handleMaxScoreClick = () => {
      // Only allow editing if we're in fine-tune mode (editingSolutionId === subQuestion.id)
      if (editingSolutionId === subQuestion.id) {
        // Clear criteria editing state to prevent focus issues
        setEditingCriteriaId(null);
        setEditMaxValue(maxScore.toString()); // Initialize with current max score
        setIsEditingMaxScore(true);
      }
    };

    const handleMaxInputChange = e => {
      setEditMaxValue(e.target.value);
    };

    const saveEditedMaxScore = () => {
      let newMaxScore = parseFloat(editMaxValue);
      // Round to max 2 decimal places
      newMaxScore = Math.round(newMaxScore * 100) / 100;

      setIsEditingMaxScore(false);

      // Update our states to maintain the new value
      if (newMaxScore !== tempMaxScore) {
        setTempMaxScore(newMaxScore);
        setMaxScore(newMaxScore);
        setEditMaxValue(newMaxScore.toString());

        // Store the value in a data attribute on the element for retrieval during regrade
        const element = document.getElementById(`subquestion-${subQuestion.id}`);
        if (element) {
          element.setAttribute('data-max-weight', newMaxScore.toString());
        }
      }

      // Even if the value hasn't changed, still update the data attribute to ensure consistency
      const element = document.getElementById(`subquestion-${subQuestion.id}`);
      if (element) {
        element.setAttribute('data-max-weight', newMaxScore.toString());
      }
    };

    const handleMaxInputBlur = () => {
      saveEditedMaxScore();
    };

    const handleMaxInputKeyDown = e => {
      if (e.key === 'Enter') {
        saveEditedMaxScore();
      } else if (e.key === 'Escape') {
        setEditMaxValue(maxScore.toString()); // Revert to original max score
        setIsEditingMaxScore(false);
      }
    };
    // === END NEW HANDLERS ===

    // Function to determine color based on the score
    const getColorForScore = score => {
      const percentage = score / maxScore;

      if (percentage >= 1) {
        // Fully correct: premium gold/green gradient
        return {
          color: COLORS.green,
          solidColor: COLORS.green,
          gradient: `linear-gradient(to right, ${COLORS.green}, ${COLORS.green})`,
          lightGradient: `linear-gradient(to right, rgba(16, 185, 129, 0.15), rgba(16, 185, 129, 0.3))`,
          shadowColor: 'rgba(16, 185, 129, 0.4)'
        };
      } else {
        // Partially correct: amber/gold gradient
        return {
          color: COLORS.primary,
          solidColor: COLORS.primary,
          gradient: `linear-gradient(to right, ${COLORS.primary}, ${COLORS.primaryDark})`,
          lightGradient: `linear-gradient(to right, rgba(228, 177, 122, 0.15), rgba(201, 155, 105, 0.3))`,
          shadowColor: 'rgba(228, 177, 122, 0.4)'
        };
      }
    };

    const colors = getColorForScore(displayScore);

    return (
      <div
        // Reduce padding from p-6 to p-4
        className="scale-container p-4 rounded-xl border border-gray-200 bg-white shadow-sm animate-scale-entrance transition-all duration-300"
        style={{
          background: `linear-gradient(to bottom, white, ${COLORS.bgLight})`
        }}
      >
        <StyleTag />

        {/* Score display header - better centered and with higher contrast */}
        <div className="mb-3 flex justify-center items-center">
          <div className="flex items-baseline gap-2 justify-center text-center w-full">
            <div
              className="text-3xl font-bold flex items-baseline"
              style={{ color: COLORS.textPrimary }}
            >
              {/* === CONDITIONAL RENDERING for score === */}
              {isEditingScore ? (
                <input
                  type="number"
                  value={editValue}
                  className="box-border hide-number-spinners font-bold text-center align-baseline outline-none border-b-2 border-amber-500 bg-transparent rounded-md px-1 py-0 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-500 leading-none"
                  style={{
                    color: colors.solidColor,
                    width: `${maxScore.toString().length + 3}ch`, // May need adjustment for larger font
                    outline: 'none !important',
                    fontSize: '1.9rem', // Explicitly EVEN larger font size (like text-8xl)
                    lineHeight: '1'
                  }}
                  step="0.5"
                  min="0"
                  max={maxScore}
                  autoFocus // Focus when it appears
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                  onKeyDown={handleInputKeyDown}
                />
              ) : (
                <span
                  className="text-6xl cursor-pointer hover:opacity-80 transition-opacity" // Increased size
                  style={{ color: colors.solidColor }}
                  onClick={handleScoreClick} // Click to edit
                >
                  {displayScore}
                </span>
              )}
              {/* === END CONDITIONAL RENDERING === */}
              <span className="mx-1 text-2xl text-gray-600">/</span>
              {/* === CONDITIONAL RENDERING for max score === */}
              {isEditingMaxScore && editingSolutionId === subQuestion.id ? (
                <input
                  type="number"
                  value={editMaxValue}
                  className="box-border hide-number-spinners font-bold text-center align-baseline outline-none border-b-2 border-amber-500 bg-transparent rounded-md px-1 py-0 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-500 leading-none"
                  style={{
                    color: '#8c6b4a',
                    width: `${editMaxValue.length + 3}ch`,
                    outline: 'none !important',
                    fontSize: '1.9rem',
                    lineHeight: '1'
                  }}
                  step="0.5"
                  min="0"
                  max="20"
                  autoFocus
                  onChange={handleMaxInputChange}
                  onBlur={handleMaxInputBlur}
                  onKeyDown={handleMaxInputKeyDown}
                />
              ) : (
                <div className="flex items-center">
                  <span
                    className={`text-2xl ${editingSolutionId === subQuestion.id ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
                    style={{
                      textDecoration:
                        editingSolutionId === subQuestion.id ? 'underline dotted' : 'none',
                      textDecorationColor:
                        editingSolutionId === subQuestion.id ? 'rgba(228, 177, 122, 0.5)' : 'none'
                    }}
                    onClick={handleMaxScoreClick} // Click handler will check if editing is allowed
                  >
                    {maxScore}
                  </span>
                  {/* Pencil icon that appears only when grading criteria editor is open */}
                </div>
              )}
              {/* === END CONDITIONAL RENDERING === */}
              <span className="ml-2 text-base font-medium text-gray-700">points</span>
            </div>
          </div>
        </div>

        {/* Score scale slider */}
        {/* Reduce height from h-20 to h-16, remove mt-1 */}
        <div
          ref={sliderRef}
          className="relative h-16 flex items-center cursor-pointer mt-0"
          onClick={handleSliderClick}
          onMouseMove={handleSliderHover}
          onMouseLeave={handleMouseLeave}
        >
          {/* Track background with subtle gradient */}
          <div
            className="absolute h-2 w-full rounded-full top-1/2 transform -translate-y-1/2"
            style={{
              background: COLORS.lightGray,
              boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
            }}
          ></div>

          {/* Filled portion of the track */}
          <div
            className="absolute h-2 rounded-full scale-fill top-1/2 transform -translate-y-1/2"
            style={{
              width: `${(displayScore / maxScore) * 100}%`,
              background: colors.gradient,
              boxShadow: `0 1px 3px ${colors.shadowColor}`
            }}
          ></div>

          {/* Improved tooltip with better visibility and contrast */}
          {hoverScore !== null && hoverScore !== displayScore && (
            <div
              className="absolute"
              style={{
                left: `${(hoverScore / maxScore) * 100}%`,
                bottom: '-32px',
                transform: 'translateX(-50%)',
                width: '46px',
                height: '28px',
                zIndex: 50
              }}
            >
              <div
                className="mx-auto text-center bg-white rounded-md px-3 py-1 shadow-md"
                style={{
                  background: COLORS.bgWhite,
                  color: getColorForScore(hoverScore).solidColor,
                  border: `1.5px solid ${getColorForScore(hoverScore).solidColor}`,
                  fontSize: '14px',
                  fontWeight: 600,
                  lineHeight: 1.2,
                  width: 'fit-content'
                }}
              >
                {hoverScore}
              </div>
            </div>
          )}

          {/* Hover indicator */}
          {hoverScore !== null && (
            <div
              className="absolute w-4 h-4 rounded-full bg-white shadow-md transform -translate-x-1/2 -translate-y-1/2 z-20"
              style={{
                left: `${(hoverScore / maxScore) * 100}%`,
                border: `2px solid ${getColorForScore(hoverScore).solidColor}`,
                top: '50%',
                transition: 'left 0.1s ease'
              }}
            ></div>
          )}

          {/* The score points (dots) with invisible larger click areas */}
          {scorePoints.map((score, index) => {
            const isSelected = score <= displayScore;
            // Use whole number check for dot size consistency
            const isWholeNumber = score % 1 === 0;
            const isCurrentScore = score === displayScore;

            // Reduce whole number dot size but keep it larger than half-point dots
            const dotSize = isWholeNumber ? 'w-6 h-6' : 'w-3 h-3';

            // Add a large invisible click area that doesn't affect visuals
            const clickAreaSize = 'w-10 h-10';

            return (
              <div
                key={index}
                className={`scale-dot absolute transform -translate-x-1/2 -translate-y-1/2 ${clickAreaSize} cursor-pointer z-10 flex items-center justify-center`}
                style={{
                  left: `${(score / maxScore) * 100}%`,
                  top: '50%'
                }}
                onClick={() => {
                  // Simple direct call to handleDotClick
                  handleDotClick(score);
                }}
                onMouseEnter={() => setHoverScore(score)}
                onMouseLeave={() => setHoverScore(null)}
              >
                <div
                  className={`${dotSize} rounded-full flex items-center justify-center`}
                  style={{
                    backgroundColor: isSelected ? colors.solidColor : '#fff',
                    border: `2px solid ${isSelected ? colors.solidColor : COLORS.gray}`,
                    // Apply transparent shadow when not current to reserve space
                    boxShadow: isCurrentScore
                      ? `0 0 0 2px ${colors.shadowColor}`
                      : '0 0 0 2px transparent',
                    transition:
                      'background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease'
                  }}
                >
                  {/* Display numbers inside the dots for whole numbers */}
                  {isWholeNumber && (
                    <span
                      className="text-sm font-bold"
                      style={{
                        color: isSelected ? 'white' : colors.solidColor
                      }}
                    >
                      {score}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Removed the separate score labels section since numbers are now in the dots */}
      </div>
    );
  };

  // State to track rendered questions for animations
  const [renderedQuestions, setRenderedQuestions] = useState([]);
  const [renderedSubQuestions, setRenderedSubQuestions] = useState({});

  // State to track changed max weights for subquestions
  const [changedMaxWeights, setChangedMaxWeights] = useState({});

  // Effect to update rendered questions for animations
  useEffect(() => {
    // Track which questions and subquestions should be visible
    if (pinnedSubQuestionId) {
      // If something is pinned, only keep track of questions containing the pinned subquestion
      const newRenderedQuestions = examPaper
        .filter(q => q.subQuestions.some(sq => sq.id === pinnedSubQuestionId))
        .map(q => q.id);

      // For subquestions, only keep the pinned one
      const newRenderedSubQuestions = {};
      newRenderedQuestions.forEach(qId => {
        newRenderedSubQuestions[qId] = [pinnedSubQuestionId];
      });

      setRenderedQuestions(newRenderedQuestions);
      setRenderedSubQuestions(newRenderedSubQuestions);
    } else {
      // If nothing is pinned, all questions and subquestions are visible
      setRenderedQuestions(examPaper.map(q => q.id));

      const allSubQuestions = {};
      examPaper.forEach(q => {
        allSubQuestions[q.id] = q.subQuestions.map(sq => sq.id);
      });
      setRenderedSubQuestions(allSubQuestions);
    }
  }, [pinnedSubQuestionId, examPaper]);

  // Add state variables for solution editing
  const [editingSolutionId, setEditingSolutionId] = useState(null);
  const [editedSolutions, setEditedSolutions] = useState({});
  const [editingCriteriaId, setEditingCriteriaId] = useState(null);

  // Handler for toggling the solution editor
  const toggleSolutionEditor = subQuestionId => {
    if (editingSolutionId === subQuestionId) {
      setEditingSolutionId(null);
      setEditingCriteriaId(null);
      // Reset changed max weights state when closing editor
      setChangedMaxWeights({});

      // No need to reset data-max-weight here as it's now handled in the Cancel button click handler
    } else {
      setEditingSolutionId(subQuestionId);
      // Reset changed max weights state when opening editor
      setChangedMaxWeights({});

      // When opening the editor, reset the displayed max weight to the original value
      const element = document.getElementById(`subquestion-${subQuestionId}`);
      if (element) {
        // Find the original max_weight from the subquestion object
        const subQ = examPaper.flatMap(q => q.subQuestions).find(sq => sq.id === subQuestionId);
        const originalMaxWeight = subQ?.max_weight || 1;
        // Set the data attribute to the original value
        element.setAttribute('data-max-weight', originalMaxWeight.toString());
      }

      // Initialize the edited solution if not already present
      if (!editedSolutions[subQuestionId]) {
        const subQ = examPaper.flatMap(q => q.subQuestions).find(sq => sq.id === subQuestionId);

        setEditedSolutions(prev => ({
          ...prev,
          [subQuestionId]: subQ?.solution || ''
        }));
      }
    }
  };

  const handleSaveSolution = async (subQuestionId, submissionIds) => {
    try {
      // Find the subquestion in the exam paper
      const subQ = examPaper.flatMap(q => q.subQuestions).find(sq => sq.id === subQuestionId);

      // Get the updated max_weight from our data attribute if it exists
      let updatedMaxWeight = subQ?.max_weight || 1;
      const element = document.getElementById(`subquestion-${subQuestionId}`);

      if (element) {
        // First check for our data attribute where we stored the most recent value
        const dataMaxWeight = element.getAttribute('data-max-weight');
        if (dataMaxWeight) {
          updatedMaxWeight = parseFloat(dataMaxWeight);
        }
      }

      // If we couldn't find the value in the DOM, use the edited solutions state
      if (updatedMaxWeight === null && editedSolutions[subQuestionId]) {
        updatedMaxWeight = parseFloat(tempMaxScores[subQuestionId]);
      }

      // If we still don't have a max weight, use a default of 5
      if (updatedMaxWeight === null || isNaN(updatedMaxWeight)) {
        updatedMaxWeight = 5; // Default
      }

      // Build the request body with optional submissionIds
      const requestBody = {
        subQuestionId: subQuestionId,
        gradingCriteria: subQ?.gradingCriteria || '',
        solution: editedSolutions[subQuestionId],
        maxWeight: updatedMaxWeight
      };

      // Only add submissionIds if they are provided
      if (submissionIds && submissionIds.length > 0) {
        requestBody.submissionIds = submissionIds;
      }

      // Use the same API endpoint as in handleConfirmRegrade
      await gradeApi.post({
        path: `/api/assignment/${assignment_id}/ai-regrade/`,
        body: requestBody,
        config: {
          headers: {
            Authorization: `Bearer ${user?.session_token}`
          }
        }
      });

      // Close the solution editor
      setEditingSolutionId(null);
      setEditingCriteriaId(null);

      // Display success message using toast
      toast.success('AI-Regrade initiated. The system will update the grades shortly.');

      // Start polling for regrade completion
      startPollingForRegradeCompletion(subQuestionId);

      // Refresh data
      if (getQuestions) {
        getQuestions();
      }

      if (getAssignmentSubmission) {
        getAssignmentSubmission();
      }
    } catch (error) {
      console.error('AI-Regrade failed:', error);
      toast.error('AI-Regrade failed. Please try again.');
    }
  };

  // Handler for updating the solution text
  const handleSolutionChange = (subQuestionId, value) => {
    setEditedSolutions(prev => ({
      ...prev,
      [subQuestionId]: value
    }));

    // Mark this subquestion as having its criteria being edited
    setEditingCriteriaId(subQuestionId);

    // Ensure the textarea keeps focus after typing
    setTimeout(() => {
      const activeElement = document.getElementById(`textarea-${subQuestionId}`);
      if (activeElement) {
        activeElement.focus();
      }
    }, 0);
  };

  // Add a ref for the textarea to maintain focus
  const textareaRef = useRef(null);

  // Function to handle AI regrade action
  const handleAIRegrade = async subQuestionId => {
    try {
      // Get the max weight value from the DOM
      const element = document.getElementById(`subquestion-${subQuestionId}`);
      let maxWeight = null;
      if (element) {
        const dataMaxWeight = element.getAttribute('data-max-weight');
        if (dataMaxWeight) {
          maxWeight = parseFloat(dataMaxWeight);
        }
      }

      if (maxWeight === null || isNaN(maxWeight)) {
        maxWeight = 5; // Default
      }

      const subQ = examPaper.flatMap(q => q.subQuestions).find(sq => sq.id === subQuestionId);

      const requestBody = {
        subQuestionId: subQuestionId,
        gradingCriteria: subQ?.gradingCriteria || '',
        solution: editedSolutions[subQuestionId],
        maxWeight: maxWeight
      };

      // Send to API and wait for success response
      const response = await gradeApi.post({
        path: `/api/assignment/${assignment_id}/ai-regrade/`,
        body: requestBody,
        config: {
          headers: {
            Authorization: `Bearer ${user?.session_token}`
          }
        }
      });

      // Only proceed if we got a successful response
      if (response && (response.status === 200 || response.status === 201 || response.success)) {
        // Close the solution editor
        setEditingSolutionId(null);
        setEditingCriteriaId(null);

        // Display success message
        toast.success('AI-Regrade initiated. The system will update the grades shortly.');

        // Start polling for regrade completion
        startPollingForRegradeCompletion(subQuestionId);

        // Initial data refresh to get the updated regradeInProgress status
        if (getQuestions) {
          await getQuestions();
        }
      } else {
        throw new Error('Failed to initiate regrade');
      }
    } catch (error) {
      console.error('AI-Regrade failed:', error);
      toast.error('AI-Regrade failed. Please try again.');
    }
  };

  // Main rendering
  return (
    <>
      <StyleTag />
      {/* Only render directly from examPaper when no specific subQuestion is provided */}
      {!subQuestion &&
        examPaper.map((question, qIndex) => {
          const isVisible = renderedQuestions.includes(question.id);
          const shouldRender = pinnedSubQuestionId
            ? question.subQuestions.some(sq => sq.id === pinnedSubQuestionId)
            : true;

          if (!shouldRender && !isVisible) {
            return null;
          }

          return (
            <div
              key={qIndex}
              className="mb-8 transition-all duration-300 ease-in-out overflow-visible"
              style={{
                opacity: isVisible ? '1' : '0',
                height: isVisible ? 'auto' : '0',
                marginBottom: isVisible ? '2rem' : '0'
              }}
            >
              {question.subQuestions.map((sq, sqIndex) => {
                // Use the mapped one since subQuestion prop is not provided here
                const currentSubQuestion = sq;
                // Use the array indices
                const currentQuestionIndex = qIndex;
                const currentSubQuestionIndex = sqIndex;

                const isSubVisible = renderedSubQuestions[question.id]?.includes(
                  currentSubQuestion.id
                );
                const shouldRenderSub = pinnedSubQuestionId
                  ? currentSubQuestion.id === pinnedSubQuestionId
                  : true;

                if (!shouldRenderSub && !isSubVisible) {
                  return null;
                }

                const isDisabledGrading = false; // Removed complex check for disabled grading
                const itemProps =
                  currentSubQuestion.id === regrade_question_id ? { ref: regradeRef } : {};

                return (
                  <div
                    key={sqIndex}
                    id={`subquestion-${currentSubQuestion.id}`}
                    className={`mb-6 ml-4 transition-all duration-300 ease-in-out relative ${regradeInProgress[currentSubQuestion.id] ? 'opacity-80 cursor-not-allowed pointer-events-none' : ''}`}
                    style={{
                      opacity: isSubVisible ? '1' : '0',
                      height: isSubVisible ? 'auto' : '0',
                      marginBottom: isSubVisible ? '1.5rem' : '0',
                      transform:
                        pinnedSubQuestionId === currentSubQuestion.id ? 'scale(1.02)' : 'scale(1)',
                      padding: pinnedSubQuestionId === currentSubQuestion.id ? '1rem' : '0',
                      borderRadius: pinnedSubQuestionId === currentSubQuestion.id ? '0.5rem' : '0'
                    }}
                    {...itemProps}
                    data-subquestion-id={currentSubQuestion.id}
                  >
                    {regradeInProgress[currentSubQuestion.id] && (
                      <>
                        <div className="mt-2 mb-4 p-3 bg-blue-50 border-l-4 border-blue-400 text-blue-700 rounded">
                          ⏳ This subquestion is being regraded...
                        </div>
                        {/* Add an overlay to visually indicate the rubric is not interactable */}
                        <div
                          className="absolute inset-0 bg-gray-100 bg-opacity-50 z-10 rounded-lg flex items-center justify-center"
                          style={{ pointerEvents: 'none' }}
                        ></div>
                      </>
                    )}

                    {/* Question header */}
                    <div className="mb-3">
                      {/* Question header styling - simple styling approach */}
                      {
                        <>
                          {/* Main question with text on the same line */}
                          {sqIndex === 0 && (
                            <div className="mb-4">
                              <div className="flex items-baseline justify-between mb-1">
                                <div className="flex items-baseline flex-grow">
                                  <TruncatedText
                                    text={question.description}
                                    className="text-gray-700 font-medium text-lg"
                                    maxLength={70}
                                  />
                                </div>
                                {/* Pin button moved to overarching question level when no subquestions */}
                                {question.subQuestions.length === 1 &&
                                  !currentSubQuestion.question && (
                                    <button
                                      style={{
                                        transition: 'all 0.3s ease',
                                        transform:
                                          pinnedSubQuestionId === currentSubQuestion.id
                                            ? 'scale(1.1)'
                                            : 'scale(1)',
                                        boxShadow:
                                          pinnedSubQuestionId === currentSubQuestion.id
                                            ? '0 4px 6px rgba(0, 0, 0, 0.1)'
                                            : 'none'
                                      }}
                                      className={`inline-flex items-center px-4 py-2 rounded-full ${pinnedSubQuestionId === currentSubQuestion.id ? 'bg-[#e4b17a] text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                                      title={
                                        pinnedSubQuestionId === currentSubQuestion.id
                                          ? 'Unpin this subquestion'
                                          : 'Pin this subquestion'
                                      }
                                      onClick={e => {
                                        e.stopPropagation();
                                        // Flash animation effect
                                        const element = document.getElementById(
                                          `subquestion-${currentSubQuestion.id}`
                                        );
                                        if (
                                          element &&
                                          pinnedSubQuestionId !== currentSubQuestion.id
                                        ) {
                                          // Apply flash effect
                                          element.style.transition = 'all 0.5s ease';
                                          setTimeout(() => {
                                            if (pinnedSubQuestionId !== currentSubQuestion.id) {
                                              element.style.backgroundColor = '';
                                            }
                                          }, 500);
                                        }
                                        togglePinSubQuestion(currentSubQuestion.id);
                                      }}
                                    >
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-5 w-5"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                      >
                                        <path d="M19 3.5l-3 2V11c0 1.2.5 2.3 1.4 3.3l.6.6V21l-6-1l-6 1v-6.1l.6-.6c.9-1 1.4-2.1 1.4-3.3V5.5l-3-2 2-2h10l2 2z" />
                                      </svg>
                                      <span className="text-sm font-medium">
                                        {pinnedSubQuestionId === currentSubQuestion.id
                                          ? 'Unpin'
                                          : 'Pin'}
                                      </span>
                                    </button>
                                  )}
                              </div>
                            </div>
                          )}
                          {/* Subquestion with consistent styling - check if it's the only subquestion with blank text */}
                          <div className="mb-3">
                            <div className="flex items-baseline justify-between mb-1">
                              <div className="flex items-baseline flex-grow">
                                {/* If there's only one subquestion with blank text, don't show the subquestion letter */}
                                <span className="text-gray-700 mr-2">
                                  {question.subQuestions.length === 1 &&
                                  !currentSubQuestion.question
                                    ? ``
                                    : `${currentQuestionIndex + 1}${String.fromCharCode(97 + currentSubQuestionIndex)}) `}
                                </span>
                                <TruncatedText
                                  text={currentSubQuestion.question}
                                  className="text-gray-700"
                                  maxLength={70}
                                />
                              </div>
                              {/* Only show pin button here if not already shown at the question level */}
                              {!(
                                question.subQuestions.length === 1 && !currentSubQuestion.question
                              ) && (
                                <button
                                  style={{
                                    transition: 'all 0.3s ease',
                                    transform:
                                      pinnedSubQuestionId === currentSubQuestion.id
                                        ? 'scale(1.1)'
                                        : 'scale(1)',
                                    boxShadow:
                                      pinnedSubQuestionId === currentSubQuestion.id
                                        ? '0 4px 6px rgba(0, 0, 0, 0.1)'
                                        : 'none'
                                  }}
                                  className={`inline-flex items-center px-4 py-2 rounded-full ${pinnedSubQuestionId === currentSubQuestion.id ? 'bg-[#e4b17a] text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                                  title={
                                    pinnedSubQuestionId === currentSubQuestion.id
                                      ? 'Unpin this subquestion'
                                      : 'Pin this subquestion'
                                  }
                                  onClick={e => {
                                    e.stopPropagation();
                                    // Flash animation effect
                                    const element = document.getElementById(
                                      `subquestion-${currentSubQuestion.id}`
                                    );
                                    if (element && pinnedSubQuestionId !== currentSubQuestion.id) {
                                      // Apply flash effect
                                      element.style.transition = 'all 0.5s ease';
                                      setTimeout(() => {
                                        if (pinnedSubQuestionId !== currentSubQuestion.id) {
                                          element.style.backgroundColor = '';
                                        }
                                      }, 500);
                                    }
                                    togglePinSubQuestion(currentSubQuestion.id);
                                  }}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-5 w-5"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                  >
                                    <path d="M19 3.5l-3 2V11c0 1.2.5 2.3 1.4 3.3l.6.6V21l-6-1l-6 1v-6.1l.6-.6c.9-1 1.4-2.1 1.4-3.3V5.5l-3-2 2-2h10l2 2z" />
                                  </svg>
                                  <span className="text-sm font-medium">
                                    {pinnedSubQuestionId === currentSubQuestion.id
                                      ? 'Unpin'
                                      : 'Pin'}
                                  </span>
                                </button>
                              )}
                            </div>
                          </div>
                        </>
                      }
                      {/* Pin button has been moved to be inline with question above */}
                      {/* Essay question styling */}
                    </div>

                    {/* Score scale section with fine-tune button */}
                    <div className="flex flex-col gap-2 mb-2 mt-3 relative">
                      {/* Inline Solution Editor - moved above the scale */}
                      {editingSolutionId === currentSubQuestion.id && (
                        <div
                          className="w-full mb-2 animate-solution-fade-in solution-editor"
                          style={{
                            background: 'rgb(255, 255, 255)',
                            borderRadius: '1rem',
                            border: '1px solid rgba(228, 177, 122, 0.12)',
                            boxShadow:
                              'rgba(228, 177, 122, 0.06) 0px 2px 4px -2px, rgba(228, 177, 122, 0.04) 0px 4px 8px -2px',
                            padding: '1rem',
                            zIndex: 1000,
                            isolation: 'isolate'
                          }}
                        >
                          <style jsx>{`
                            @keyframes solutionFadeIn {
                              0% {
                                opacity: 0;
                                transform: translateY(10px);
                              }
                              100% {
                                opacity: 1;
                                transform: translateY(0px);
                              }
                            }
                          `}</style>
                          <div className="flex justify-between items-center mb-3">
                            <h3 className="text-base font-medium text-[#8c6b4a]">
                              Edit Grading Criteria
                            </h3>
                            <div className="flex items-center gap-3">
                              <button
                                className="edit-ai-btn"
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  padding: '5px 10px',
                                  borderRadius: '4px',
                                  color: '#fff',
                                  background: 'linear-gradient(to right, #e4b17a, #c99b69)',
                                  border: 'none',
                                  fontWeight: '500',
                                  fontSize: '0.85rem',
                                  cursor: 'pointer',
                                  transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
                                }}
                                title="Enhance with AI"
                                onClick={() => handleAiEditClick(currentSubQuestion)}
                              >
                                <Sparkles
                                  size={18}
                                  style={{ marginRight: '6px', color: '#fff', flexShrink: 0 }}
                                />
                                Edit with AI
                              </button>

                              <div className="flex items-center bg-gray-50 border border-gray-200 rounded-md shadow-sm">
                                <button
                                  className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-200 rounded-l-md focus:outline-none transition-colors"
                                  title="Decrease max score by 0.5"
                                  onClick={() => {
                                    if (editingSolutionId === currentSubQuestion.id) {
                                      const element = document.getElementById(
                                        `subquestion-${currentSubQuestion.id}`
                                      );
                                      let currentScore = parseFloat(
                                        currentSubQuestion.max_weight || 1
                                      );
                                      if (element) {
                                        const dataMaxWeight =
                                          element.getAttribute('data-max-weight');
                                        if (dataMaxWeight) {
                                          currentScore = parseFloat(dataMaxWeight);
                                        }
                                      }
                                      const newScore = Math.max(
                                        0,
                                        parseFloat((currentScore - 0.5).toFixed(1))
                                      );
                                      if (element) {
                                        element.setAttribute(
                                          'data-max-weight',
                                          newScore.toString()
                                        );
                                      }
                                      const originalMaxWeight = currentSubQuestion.max_weight || 1;
                                      const hasChanged =
                                        Math.abs(newScore - originalMaxWeight) > 0.001;
                                      setChangedMaxWeights(prev => ({
                                        ...prev,
                                        [currentSubQuestion.id]: hasChanged
                                      }));
                                      setRenderedQuestions([...renderedQuestions]);
                                    }
                                  }}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-5 w-5"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </button>

                                <div className="flex items-center px-2">
                                  <span className="text-sm font-medium text-gray-700 whitespace-nowrap mr-1">
                                    Max:
                                  </span>
                                  <span
                                    className="inline-block w-10 text-center font-medium text-gray-800"
                                    style={{ minWidth: '2.5rem' }}
                                  >
                                    {(() => {
                                      const element = document.getElementById(
                                        `subquestion-${currentSubQuestion.id}`
                                      );
                                      let value;
                                      if (element) {
                                        const dataMaxWeight =
                                          element.getAttribute('data-max-weight');
                                        if (dataMaxWeight) {
                                          value = parseFloat(dataMaxWeight);
                                        } else {
                                          value = parseFloat(currentSubQuestion.max_weight || 1);
                                        }
                                      } else {
                                        value = parseFloat(currentSubQuestion.max_weight || 1);
                                      }
                                      if (value === 0) {
                                        return '0';
                                      }
                                      return value % 1 === 0 ? value.toFixed(0) : value.toFixed(1);
                                    })()}
                                  </span>
                                </div>

                                <button
                                  className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-200 rounded-r-md focus:outline-none transition-colors"
                                  title="Increase max score by 0.5"
                                  onClick={() => {
                                    if (editingSolutionId === currentSubQuestion.id) {
                                      const element = document.getElementById(
                                        `subquestion-${currentSubQuestion.id}`
                                      );
                                      let currentScore = parseFloat(
                                        currentSubQuestion.max_weight || 1
                                      );
                                      if (element) {
                                        const dataMaxWeight =
                                          element.getAttribute('data-max-weight');
                                        if (dataMaxWeight) {
                                          currentScore = parseFloat(dataMaxWeight);
                                        }
                                      }
                                      const newScore = Math.min(
                                        20,
                                        parseFloat((currentScore + 0.5).toFixed(1))
                                      );
                                      if (element) {
                                        element.setAttribute(
                                          'data-max-weight',
                                          newScore.toString()
                                        );
                                      }
                                      const originalMaxWeight = currentSubQuestion.max_weight || 1;
                                      const hasChanged =
                                        Math.abs(newScore - originalMaxWeight) > 0.001;
                                      setChangedMaxWeights(prev => ({
                                        ...prev,
                                        [currentSubQuestion.id]: hasChanged
                                      }));
                                      setRenderedQuestions([...renderedQuestions]);
                                    }
                                  }}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-5 w-5"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 01-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          </div>

                          <textarea
                            ref={textareaRef}
                            id={`textarea-${currentSubQuestion.id}`}
                            className="w-full p-3 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400 bg-white text-gray-700 text-sm"
                            style={{
                              minHeight: '100px',
                              resize: 'vertical'
                            }}
                            value={editedSolutions[currentSubQuestion.id] || ''}
                            placeholder="Enter the solution here..."
                            onChange={e => {
                              // Auto-resize the textarea based on content
                              const textarea = e.target;
                              textarea.style.height = 'auto';
                              textarea.style.height = textarea.scrollHeight + 'px';
                              handleSolutionChange(currentSubQuestion.id, e.target.value);
                            }}
                            onClick={() => setEditingCriteriaId(currentSubQuestion.id)}
                            onFocus={e => {
                              setEditingCriteriaId(currentSubQuestion.id);
                              // Auto-resize on focus too
                              const textarea = e.target;
                              textarea.style.height = 'auto';
                              textarea.style.height = textarea.scrollHeight + 'px';
                            }}
                          />
                          <div className="flex justify-end space-x-2 mt-3">
                            <button
                              className="px-3 py-1 bg-gray-50 text-gray-600 border border-gray-200 rounded-md hover:bg-gray-100 transition-colors text-sm"
                              onClick={() => {
                                // Reset the data-max-weight attribute to the original value when canceling
                                const element = document.getElementById(
                                  `subquestion-${editingSolutionId}`
                                );
                                if (element) {
                                  // Find the original max_weight from the subquestion object
                                  const subQ = examPaper
                                    .flatMap(q => q.subQuestions)
                                    .find(sq => sq.id === editingSolutionId);
                                  const originalMaxWeight = subQ?.max_weight || 1;
                                  // Reset the data attribute
                                  element.setAttribute(
                                    'data-max-weight',
                                    originalMaxWeight.toString()
                                  );

                                  // Force re-render to show the updated value
                                  setRenderedQuestions([...renderedQuestions]);
                                }

                                // Reset changed max weights for this subquestion
                                setChangedMaxWeights(prev => ({
                                  ...prev,
                                  [editingSolutionId]: false
                                }));

                                // Close the editor
                                setEditingSolutionId(null);
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              className="px-3 py-1.5 flex items-center gap-1 text-white rounded-md text-sm hover:transform hover:-translate-y-[1px] transition-all duration-300 hover:shadow-[0_8px_24px_rgba(228,177,122,0.3)]"
                              style={{
                                background: 'linear-gradient(135deg, #e4b17a, #c99b69)',
                                boxShadow: '0 4px 12px rgba(228, 177, 122, 0.2)'
                              }}
                              onClick={() => handleAIRegrade(editingSolutionId)}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4 mr-1"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M7 16l-4-4 4-4" />
                                <path d="M17 8l4 4-4 4" />
                                <path d="M3 12h18" />
                              </svg>
                              Regrade All
                            </button>
                            {sortedSubmissionIds && remainingCount > 0 && (
                              <button
                                className={`px-3 py-1.5 flex items-center gap-1 text-white rounded-md text-sm hover:transform hover:-translate-y-[1px] transition-all duration-300 hover:shadow-[0_8px_24px_rgba(228,177,122,0.3)] ${
                                  changedMaxWeights[editingSolutionId]
                                    ? 'opacity-50 cursor-not-allowed'
                                    : ''
                                }`}
                                style={{
                                  background: 'linear-gradient(135deg, #e4b17a, #c99b69)',
                                  boxShadow: '0 4px 12px rgba(228, 177, 122, 0.2)'
                                }}
                                disabled={changedMaxWeights[editingSolutionId]}
                                title={
                                  changedMaxWeights[editingSolutionId]
                                    ? 'Regrade Next is disabled when max points have been changed'
                                    : `Regrade the next ${remainingCount} submissions`
                                }
                                onClick={() =>
                                  !changedMaxWeights[editingSolutionId] &&
                                  handleSaveSolution(editingSolutionId, remainingSubmissionIds)
                                }
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-4 w-4 mr-1"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M5 12h14" />
                                  <path d="M12 5l7 7-7 7" />
                                </svg>
                                This + Next {remainingCount}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                      {/* Fine Tune Grading button - top-right of scale */}
                      <div className="absolute top-0 right-0 z-10">
                        <button
                          className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[rgba(228,177,122,0.1)] border border-[rgba(228,177,122,0.2)] text-[#8c6b4a] font-medium text-sm transition-all duration-300 hover:bg-[rgba(228,177,122,0.15)] hover:transform hover:-translate-y-[1px] hover:shadow-[0_4px_12px_rgba(228,177,122,0.1)]"
                          onClick={() => toggleSolutionEditor(currentSubQuestion.id)}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-4 h-4 text-[#e4b17a]"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
                          </svg>
                          <span>
                            {editingSolutionId === currentSubQuestion.id ? '' : 'Fine Tune'}
                          </span>
                        </button>
                      </div>

                      {/* Viewed indicator - top-left of scale */}
                      {isSubQuestionViewed(currentSubQuestion.id) && (
                        <div className="absolute top-0 left-0 z-10">
                          <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-green-50 border border-green-100 text-green-600">
                            <EyeIcon className="w-4 h-4" />
                            <span className="text-xs font-medium">Viewed</span>
                          </div>
                        </div>
                      )}
                      <ScoreScale subQuestion={currentSubQuestion} />
                    </div>

                    {/* Feedback section - handled by separate FeedbackPanel */}
                    <div className="mt-4">
                      {!isEditing(currentSubQuestion.id) && (
                        <FeedbackPanel
                          subQuestion={currentSubQuestion}
                          getFeedback={getFeedback}
                          handleRemoveFeedback={handleRemoveFeedback}
                          handleEditFeedback={handleEditFeedback}
                          handleFeedbackChange={handleFeedbackChange}
                          handleFeedbackSave={handleFeedbackSave}
                          handleFeedbackCancel={handleFeedbackCancel}
                        />
                      )}
                    </div>

                    {/* Regrade request UI */}
                    {getRR && getRR(currentSubQuestion.id) && (
                      <div className="mt-4 p-4 bg-gradient-to-r from-[rgba(228,177,121,0.08)] to-[rgba(228,177,121,0.04)] border-l-4 border-[#e4b17a] rounded-lg transition-all duration-200 hover:shadow-sm">
                        <div className="flex justify-between items-start">
                          <h4 className="font-medium text-[#8c6b4a] mb-2 flex items-center gap-2">
                            <ExclamationCircleIcon className="w-5 h-5 text-[#e4b17a]" />
                            Regrade Request
                          </h4>
                          <button
                            className="px-3 py-1 bg-white text-[#c99b69] border border-[#e4b17a] rounded-md hover:bg-[#fdf7f1] hover:text-[#b38757] transition-colors font-medium flex items-center gap-1 shadow-sm"
                            onClick={() => handleRegradeReq(currentSubQuestion.id)}
                          >
                            <XMarkIcon className="w-4 h-4" />
                            Dismiss
                          </button>
                        </div>
                        <p className="text-[#a17c57]">
                          {getRRText && getRRText(currentSubQuestion.id)}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      {/* When a specific subQuestion is provided, only render that one */}
      {subQuestion && (
        <div className="mb-8 transition-all duration-300 ease-in-out overflow-visible">
          {Object.keys(subQuestion).includes('question') && (
            <pre className="hidden">{JSON.stringify(subQuestion, null, 2)}</pre>
          )}
          <div
            ref={subQuestion.id === regrade_question_id ? regradeRef : null}
            id={`subquestion-${subQuestion.id}`}
            className={`mb-6 ml-4 transition-all duration-300 ease-in-out relative ${regradeInProgress[subQuestion.id] ? 'opacity-80 cursor-not-allowed pointer-events-none' : ''}`}
            style={{
              opacity: 1,
              height: 'auto',
              marginBottom: '1.5rem',
              transform: pinnedSubQuestionId === subQuestion.id ? 'scale(1.02)' : 'scale(1)',
              padding: pinnedSubQuestionId === subQuestion.id ? '1rem' : '0',
              borderRadius: pinnedSubQuestionId === subQuestion.id ? '0.5rem' : '0'
            }}
            data-subquestion-id={subQuestion.id}
          >
            {regradeInProgress[subQuestion.id] && (
              <>
                <div className="mt-2 mb-4 p-3 bg-blue-50 border-l-4 border-blue-400 text-blue-700 rounded">
                  ⏳ This subquestion is being regraded...
                </div>
                {/* Add an overlay to visually indicate the rubric is not interactable */}
                <div
                  className="absolute inset-0 bg-gray-100 bg-opacity-50 z-10 rounded-lg flex items-center justify-center"
                  style={{ pointerEvents: 'none' }}
                ></div>
              </>
            )}

            {/* Question header */}
            <div className="mb-3">
              {/* Question header styling - simple styling approach */}
              {
                <>
                  {/* Main question with text on the same line */}
                  <div className="mb-4">
                    <div className="flex items-baseline justify-between mb-1">
                      <div className="flex items-baseline flex-grow">
                        <span className="text-gray-700 mr-2 font-medium text-lg">{`${questionIndex !== undefined ? questionIndex + 1 : '?'}) `}</span>
                        <TruncatedText
                          text={
                            examPaper.find(q => q.subQuestions.some(sq => sq.id === subQuestion.id))
                              ?.description || 'Question'
                          }
                          className="text-gray-700 font-medium text-lg"
                          maxLength={70}
                        />
                      </div>

                      {/* Pin button for this specific subquestion */}
                      <button
                        className={`transition-all ml-2 p-1 rounded-full ${
                          pinnedSubQuestionId === subQuestion.id
                            ? 'bg-amber-100 text-amber-600 hover:bg-amber-200'
                            : 'text-gray-400 hover:text-amber-500 hover:bg-amber-50'
                        }`}
                        title={
                          pinnedSubQuestionId === subQuestion.id ? 'Unpin question' : 'Pin question'
                        }
                        onClick={() => togglePinSubQuestion(subQuestion.id)}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M19 3.5l-3 2V11c0 1.2.5 2.3 1.4 3.3l.6.6V21l-6-1l-6 1v-6.1l.6-.6c.9-1 1.4-2.1 1.4-3.3V5.5l-3-2 2-2h10l2 2z" />
                        </svg>
                        <span className="text-sm font-medium">
                          {pinnedSubQuestionId === subQuestion.id ? 'Unpin' : 'Pin'}
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* Subquestion display with the identifier */}
                  <div className="flex items-start mb-2 mt-0">
                    <span className="text-gray-700 font-medium mr-2">
                      {`${String.fromCharCode(97 + (subQuestionIndex !== undefined ? subQuestionIndex : 0)).toUpperCase()}.`}
                    </span>
                    <div className="flex-1 text-gray-600">
                      <TruncatedText
                        text={
                          subQuestion.question || subQuestion.text || subQuestion.description || ''
                        }
                        maxLength={200}
                      />
                    </div>
                  </div>
                </>
              }
            </div>

            <div className="relative">
              {/* Viewed indicator - top-left of scale */}
              {isSubQuestionViewed(subQuestion.id) && (
                <div className="absolute top-0 left-0 z-10">
                  <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-green-50 border border-green-100 text-green-600">
                    <EyeIcon className="w-4 h-4" />
                    <span className="text-xs font-medium">Viewed</span>
                  </div>
                </div>
              )}
              <ScoreScale subQuestion={subQuestion} editingSolutionId={editingSolutionId} />
            </div>

            {/* Feedback section - Add this to match the other rendering path */}
            <div className="mt-4">
              {!isEditing(subQuestion.id) && (
                <FeedbackPanel
                  subQuestion={subQuestion}
                  getFeedback={getFeedback}
                  handleRemoveFeedback={handleRemoveFeedback}
                  handleEditFeedback={handleEditFeedback}
                  handleFeedbackChange={handleFeedbackChange}
                  handleFeedbackSave={handleFeedbackSave}
                  handleFeedbackCancel={handleFeedbackCancel}
                />
              )}
            </div>

            {/* Regrade request UI */}
            {getRR && getRR(subQuestion.id) && (
              <div className="mt-4 p-4 bg-gradient-to-r from-[rgba(228,177,121,0.08)] to-[rgba(228,177,121,0.04)] border-l-4 border-[#e4b17a] rounded-lg transition-all duration-200 hover:shadow-sm">
                <div className="flex justify-between items-start">
                  <h4 className="font-medium text-[#8c6b4a] mb-2 flex items-center gap-2">
                    <ExclamationCircleIcon className="w-5 h-5 text-[#e4b17a]" />
                    Regrade Request
                  </h4>
                  <button
                    className="px-3 py-1 bg-white text-[#c99b69] border border-[#e4b17a] rounded-md hover:bg-[#fdf7f1] hover:text-[#b38757] transition-colors font-medium flex items-center gap-1 shadow-sm"
                    onClick={() => handleRegradeReq(subQuestion.id)}
                  >
                    <XMarkIcon className="w-4 h-4" />
                    Dismiss
                  </button>
                </div>
                <p className="text-[#a17c57]">{getRRText && getRRText(subQuestion.id)}</p>
              </div>
            )}
          </div>
        </div>
      )}
      {showAiEditPopup && (
        <div
          className="ai-edit-popup-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}
          onClick={e => {
            if (e.target === e.currentTarget && !isProcessingAiEdit) {
              setShowAiEditPopup(false);
            }
          }}
        >
          <div
            className="ai-edit-popup"
            style={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              padding: '20px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              width: '400px',
              maxWidth: '90vw',
              display: 'flex',
              flexDirection: 'column',
              gap: '15px'
            }}
          >
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Sparkles size={20} style={{ color: '#e4b17a' }} />
              Edit Grading Criteria with AI
            </h3>
            <textarea
              ref={aiEditInputRef}
              value={aiEditInput}
              placeholder="Type your request here, e.g. 'Make the grading criteria more detailed' or 'Add a criteria for partial credit'"
              style={{
                padding: '10px 12px',
                borderRadius: '4px',
                border: '1px solid #d1d5db',
                resize: 'vertical',
                minHeight: '100px',
                fontSize: '14px',
                fontFamily: 'inherit'
              }}
              disabled={isProcessingAiEdit}
              onChange={e => setAiEditInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && e.ctrlKey && !isProcessingAiEdit) {
                  enhance_single_rubric();
                } else if (e.key === 'Escape' && !isProcessingAiEdit) {
                  setShowAiEditPopup(false);
                }
              }}
            />

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                style={{
                  padding: '8px 12px',
                  borderRadius: '4px',
                  border: '1px solid #d1d5db',
                  backgroundColor: '#f3f4f6',
                  cursor: isProcessingAiEdit ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  opacity: isProcessingAiEdit ? 0.6 : 1
                }}
                disabled={isProcessingAiEdit}
                onClick={() => !isProcessingAiEdit && setShowAiEditPopup(false)}
              >
                Cancel
              </button>
              <button
                style={{
                  padding: '8px 16px',
                  borderRadius: '4px',
                  border: 'none',
                  background: 'linear-gradient(to right, #e4b17a, #c99b69)',
                  color: '#fff',
                  cursor: isProcessingAiEdit ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: isProcessingAiEdit ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                disabled={isProcessingAiEdit}
                onClick={enhance_single_rubric}
              >
                {isProcessingAiEdit ? (
                  <>
                    <span
                      className="spinner"
                      style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        borderRadius: '50%',
                        borderTopColor: '#fff',
                        animation: 'spin 1s linear infinite'
                      }}
                    ></span>
                    Processing...
                  </>
                ) : (
                  'Confirm'
                )}
              </button>
            </div>
            <style jsx>{`
              @keyframes spin {
                to {
                  transform: rotate(360deg);
                }
              }
            `}</style>
          </div>
        </div>
      )}
    </>
  );
};

export default GradingRubric;
